/* Filament Furnace v2: small progressive enhancements. Everything works without this file (links, forms, native
 * validation, <details> menus); it only adds feedback. Motion follows prefers-reduced-motion (CSS owns the curves). */
(() => {
  const root = document.documentElement;
  root.classList.add('ff-ready');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Header: soft scroll-edge only once content is actually under the bar. */
  const header = document.querySelector('[data-ff-header]');
  if (header) {
    let ticking = false;
    const update = () => { header.classList.toggle('is-scrolled', window.scrollY > 4); ticking = false; };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  }

  /* Mobile menu (<details>): Escape closes and returns focus, outside click closes, links close it. */
  document.querySelectorAll('[data-ff-menu]').forEach((menu) => {
    const summary = menu.querySelector('summary');
    const close = (focus) => { if (!menu.open) return; menu.open = false; if (focus) summary.focus(); };
    menu.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(true); });
    document.addEventListener('click', (e) => { if (!menu.contains(e.target)) close(false); });
    menu.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => close(false)));
    menu.addEventListener('toggle', () => root.classList.toggle('ff-menu-open', menu.open));
  });

  /* Reveal on scroll: a short fade-up, once. Already-visible content is shown at once. */
  const items = document.querySelectorAll('[data-reveal]');
  if (!('IntersectionObserver' in window) || reduced.matches) {
    items.forEach((el) => el.classList.add('is-in'));
  } else {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    items.forEach((el, i) => { el.style.setProperty('--ff-i', String(i % 6)); io.observe(el); });
  }

  /* Quantity stepper. */
  document.querySelectorAll('[data-ff-qty]').forEach((q) => {
    const input = q.querySelector('input');
    q.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => {
      const min = Number(input.min || 1); const max = Number(input.max || 99);
      input.value = String(Math.min(max, Math.max(min, (Number(input.value) || min) + Number(b.dataset.step))));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }));
  });

  /* Collection: "Signature option" filter (client side; the full list stays in the HTML). */
  document.querySelectorAll('[data-ff-filter]').forEach((btn, _, all) => btn.addEventListener('click', () => {
    const grid = document.querySelector('[data-ff-grid]');
    if (!grid) return;
    const sig = btn.dataset.ffFilter === 'signature';
    all.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    let n = 0;
    grid.querySelectorAll('.ff-card').forEach((c) => { const show = !sig || c.dataset.signature === 'true'; c.hidden = !show; if (show) n++; });
    const count = document.querySelector('[data-ff-count]');
    if (count) count.textContent = `${n} ${n === 1 ? (count.dataset.one || 'design') : (count.dataset.many || 'designs')}${sig ? ' with a signature option' : ''}`;
  }));

  /* Product rails: arrow buttons scroll by roughly one screen of cards. */
  document.querySelectorAll('[data-ff-rail-prev], [data-ff-rail-next]').forEach((btn) => btn.addEventListener('click', () => {
    const section = btn.closest('section');
    const track = section && section.querySelector('[data-ff-rail-track]');
    if (!track) return;
    const dir = btn.hasAttribute('data-ff-rail-next') ? 1 : -1;
    track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: reduced.matches ? 'auto' : 'smooth' });
  }));

  /* Personalisation: live lettering preview, inline validation on leave, signature file name. */
  if (!customElements.get('ff-personalise')) {
    customElements.define('ff-personalise', class extends HTMLElement {
      connectedCallback() {
        const name = this.querySelector('[data-name]');
        const num = this.querySelector('[data-number]');
        const sig = this.querySelector('[data-signature]');
        const count = this.querySelector('[data-count]');
        const scope = this.closest('[data-ff-product]') || this;
        const pn = scope.querySelectorAll('[data-preview-name]');
        const pu = scope.querySelectorAll('[data-preview-number]');
        const max = Number(this.dataset.nameMax || 15);
        const err = (key) => this.querySelector(`[data-error-for="${key}"]`);
        const valid = {
          name: () => name.value.trim().length > 0,
          number: () => /^[0-9]{1,2}$/.test(num.value),
        };
        const show = (key, input, on) => {
          const e = err(key); if (!e) return;
          e.hidden = !on; input.setAttribute('aria-invalid', on ? 'true' : 'false');
        };
        const sync = () => {
          const caret = name.selectionStart;
          name.value = name.value.toUpperCase().slice(0, max);
          if (caret !== null) name.setSelectionRange(caret, caret);
          num.value = num.value.replace(/[^0-9]/g, '').slice(0, 2);
          const n = name.value.trim() || 'YOUR NAME';
          pn.forEach((el) => {
            el.textContent = n;
            const tile = el.closest('.ff-tile');
            if (tile) tile.style.setProperty('--len', String(n.length));
          });
          pu.forEach((el) => { el.textContent = num.value || '10'; });
          if (count) count.textContent = `${name.value.length}/${max}`;
          if (name.getAttribute('aria-invalid') === 'true' && valid.name()) show('name', name, false);
          if (num.getAttribute('aria-invalid') === 'true' && valid.number()) show('number', num, false);
        };
        name.addEventListener('input', sync);
        num.addEventListener('input', sync);
        name.addEventListener('blur', () => { if (name.value !== '' || name.dataset.touched) show('name', name, !valid.name()); name.dataset.touched = '1'; });
        num.addEventListener('blur', () => { if (num.value !== '' || num.dataset.touched) show('number', num, !valid.number()); num.dataset.touched = '1'; });
        const form = name.form;
        if (form) {
          form.addEventListener('submit', (e) => {
            const badName = !valid.name(); const badNum = !valid.number();
            show('name', name, badName); show('number', num, badNum);
            if (badName || badNum) { e.preventDefault(); (badName ? name : num).focus(); }
          });
        }
        if (sig) {
          const text = this.querySelector('[data-drop-text]');
          const clear = this.querySelector('[data-drop-clear]');
          const drop = this.querySelector('[data-drop]');
          const initial = text.textContent;
          sig.addEventListener('change', () => {
            const f = sig.files && sig.files[0];
            text.textContent = f ? f.name : initial;
            drop.classList.toggle('has-file', !!f);
            clear.hidden = !f;
          });
          clear.addEventListener('click', () => { sig.value = ''; sig.dispatchEvent(new Event('change')); drop.focus?.(); });
        }
        sync();
      }
    });
  }
})();
